import { FormEvent, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const SCRIPT_ID = 'cloudflare-turnstile-script';

export function ContactForm() {
  const widget = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string>('');
  const [siteKey, setSiteKey] = useState('');
  const [token, setToken] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState('');

  useEffect(() => {
    api<{ siteKey: string }>('/api/contact/config').then(({ siteKey: key }) => setSiteKey(key)).catch(() => setError('The contact form is temporarily unavailable.'));
  }, []);

  useEffect(() => {
    if (!siteKey || !widget.current) return;
    let cancelled = false;
    const render = () => {
      if (cancelled || !widget.current || !window.turnstile || widgetId.current) return;
      widgetId.current = window.turnstile.render(widget.current, {
        sitekey: siteKey,
        theme: 'light',
        appearance: 'interaction-only',
        callback: (value: string) => { setToken(value); setError(''); },
        'expired-callback': () => setToken(''),
        'error-callback': () => { setToken(''); setError('The security check could not load. Please try again.'); },
      });
    };
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      if (window.turnstile) render(); else existing.addEventListener('load', render, { once: true });
    } else {
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.addEventListener('load', render, { once: true });
      document.head.appendChild(script);
    }
    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current);
      widgetId.current = '';
    };
  }, [siteKey]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token) { setError('Please complete the security check.'); return; }
    setStatus('sending'); setError('');
    const form = new FormData(event.currentTarget);
    try {
      await api('/api/contact', { method: 'POST', body: JSON.stringify({ name: form.get('name'), email: form.get('email'), message: form.get('message'), turnstileToken: token }) });
      setStatus('sent');
    } catch (caught) {
      setStatus('idle'); setToken('');
      if (widgetId.current && window.turnstile) window.turnstile.reset(widgetId.current);
      setError(caught instanceof Error ? caught.message : 'Your message could not be sent.');
    }
  };

  if (status === 'sent') return <div className="contact-success" role="status"><i className="fa-solid fa-circle-check" aria-hidden="true" /><strong> Message sent.</strong><br />The organizer will get back to you.</div>;
  return (
    <form className="contact-form" onSubmit={submit}>
      <h3><i className="fa-regular fa-paper-plane" aria-hidden="true" /> Questions for us?</h3>
      <div className="contact-form-grid">
        <label>Your name<input name="name" maxLength={120} required autoComplete="name" /></label>
        <label>Email<input name="email" type="email" maxLength={254} required autoComplete="email" /></label>
      </div>
      <label>Message<textarea name="message" rows={4} maxLength={1000} required /></label>
      <div ref={widget} className="turnstile-slot" />
      {error && <p className="form-error" role="alert">{error}</p>}
      <button className="button primary full" disabled={status === 'sending' || !siteKey}>{status === 'sending' ? 'Sending…' : 'Send message'}</button>
    </form>
  );
}
